<template>
    <div>
        <p v-if="!lineItems.length" class="text-center mt-5">No order items found.</p>

        <div v-for="item in lineItems" :key="item.id" class="card general-card item-card">
            <div class="item-card-header">
                <div class="row align-items-center">
                    <div class="col-md-4 col-12">
                        <p class="mb-25 item-title">{{ item.product_name }}</p>
                        <p class="mb-0 muted-text">Order Qty: {{ item.quantity }}</p>
                    </div>
                    <div class="col-md-4 col-12">
                        <p class="mb-25">
                            Produced: {{ item.total_pq }} of {{ item.quantity }}
                            <span style="color: #6c757d">(Remaining: {{ remainingQty(item) }})</span>
                        </p>
                        <div class="progress-track">
                            <div class="progress-fill" :style="{ width: progressWidth(item.total_pq, item.quantity) }"></div>
                        </div>
                    </div>
                    <div class="col-md-4 col-12 d-flex justify-content-end align-items-center gap-1">
                        <span class="status-badge" :style="badgeStyle(itemStatus(item))">{{ convertToTitleCase(itemStatus(item)) }}</span>
                        <a href="javascript:void(0)" class="fw-bold" @click="onCreateNew(item, 'production')">+ Add Production</a>
                    </div>
                </div>
            </div>

            <div class="item-card-body">
                <div class="nested-section">
                    <ProductionPlanSection
                        :plans="plansOf(item)"
                        :onView="onViewPlan"
                        :onCreateNew="() => onOpenAddItemPlan(item)"
                        title="Plans for this item"
                        create-label="+ Add Plan"
                        empty-text="No production plan for this item yet."
                    />
                </div>

                <p class="records-title mt-1 mb-50">Production Records ({{ recordsOf(item).length }})</p>

                <p v-if="!recordsOf(item).length" class="text-center my-2">No production records for this item yet.</p>

                <div v-for="record in recordsOf(item)" :key="record.general.id" class="production-record-row">
                    <div class="row align-items-center">
                        <div class="col-md-2 col-6">
                            <p class="mb-0 fw-bold">{{ record.production.bill_number }}</p>
                            <p class="mb-0 muted-text">{{ readableDate(record.production.production_date) }}</p>
                        </div>
                        <div class="col-md-2 col-6">
                            <p class="mb-0">Qty: {{ record.general.total_quantity }} ({{ record.general.percentage }}%)</p>
                            <p class="mb-0 muted-text">Batch: {{ record.production.batch_no }} / Exp: {{ readableDate(record.production.expire_date) }}</p>
                        </div>
                        <div class="col-md-2 col-6">
                            <p class="mb-0 record-label">FG / RM Location</p>
                            <p class="mb-0">{{ record.production.finished_goods_location_name }} / {{ record.production.raw_material_location_name }}</p>
                        </div>
                        <div class="col-md-2 col-6">
                            <p class="mb-0 record-label">Product Costing</p>
                            <p class="mb-0">{{ record.production.product_costing_master_name }}</p>
                        </div>
                        <div class="col-md-2 col-6">
                            <p class="mb-0 record-label">Customer / Buyer</p>
                            <p class="mb-0">{{ record.production.contact_profile_name }} / {{ record.production.contact_buyer_name }}</p>
                        </div>
                        <div class="col-md-2 col-6">
                            <p class="mb-0 record-label">Rate / Amount</p>
                            <p class="mb-0">{{ commaFormat(record.general.rate) }} / {{ commaFormat(record.general.amount) }}</p>
                            <p class="mb-0 muted-text">FG Value: {{ commaFormat(record.production.finished_goods_value) }}</p>
                        </div>
                    </div>
                    <div class="row align-items-center mt-25">
                        <div class="col-12 d-flex justify-content-end align-items-center gap-1">
                            <a href="javascript:void(0)" class="fw-bold" @click="onClickPrint(record.production)"><i class="fas fa-print"></i></a>
                            <a href="javascript:void(0)" class="fw-bold" @click="onEditProduction(record.production)">Edit</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="unlinkedSections.length" class="card general-card">
            <div class="px-1 pb-1 pt-1">
                <p class="mb-25 fw-bold">Other Production Consumption</p>
                <ProductionSectionCard
                    v-for="section in unlinkedSections"
                    :key="section.key"
                    :title="section.title"
                    :rows="section.rows"
                    :total="section.total"
                />
            </div>
        </div>

        <AddOrEditProductionPlanModal
            v-if="store.state.isModalOpen"
            :editData="editData"
            :lockedOrder="lockedOrder"
            :lockedLineItem="lockedLineItem"
            @onSubmit="onPlanSaved"
        />

        <ProductionPlanDetailModal
            v-if="store.state.isModalOpenTwo && singlePlan"
            :plan="singlePlan"
            :redirectQuery="planRedirectQuery"
            @onEdit="onEditFromDetail"
            @onChanged="onPlanSaved"
        />

        <Loader v-if="printing"/>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { taskStatusColors, convertToTitleCase } from '@/services/utils/global.js'
import figureFormatter from '@/services/utils/figureFormatter'
import useDate from '@/services/utils/day'
import handlePurchase from '@/services/modules/purchase'
import handleBarcode from '@/services/modules/barcode'
import handleCompany from '@/services/modules/company'
import productionPDF from '@/services/utils/pdf/production'
import ProductionSectionCard from './ProductionSectionCard.vue'
import ProductionPlanSection from './ProductionPlanSection.vue'
import AddOrEditProductionPlanModal
    from '@/components/molecule/company/inventory/production-plan/AddOrEditProductionPlanModal.vue'
import ProductionPlanDetailModal
    from '@/components/molecule/company/inventory/production-plan/ProductionPlanDetailModal.vue'
import Loader from '@/components/atom/LoaderComponent'

const props = defineProps({
    orderDetail: { type: Object, required: true },
    onCreateNew: { type: Function, required: true },
    onEditProduction: { type: Function, required: true },
    onPlanChanged: { type: Function, required: true }
})

const route = useRoute()
const store = useStore()
const { commaFormat } = figureFormatter()
const { formatToDdMmYy, formatDateTime } = useDate()
const { fetchSingleProduction, fetchSingleProductionPlan } = handlePurchase()
const { fetchBarcode } = handleBarcode()
const { fetchCompanyInfo } = handleCompany()
const { exportToPDF: exportProductionPDF } = productionPDF()

const printing = ref(false)
const companyInfo = ref({})
const singlePlan = ref(null)
const editData = ref(null)
const lockedLineItem = ref(null)

const SECTION_DEFS = [
    { key: 'raw_materials', title: 'Raw Materials', match: (g) => g.type === 'raw_materials' },
    { key: 'packing_materials', title: 'Packing Materials', match: (g) => g.type === 'packing_materials' },
    { key: 'direct_labor', title: 'Direct Labor', match: (g) => g.type === 'direct_labor' },
    { key: 'direct_overhead', title: 'Direct Overhead', match: (g) => g.type === 'direct_overhead' }
]

const lineItems = computed(() => {
    if (!props.orderDetail.order_summary) return []
    if (!props.orderDetail.order_summary.line_items) return []
    return props.orderDetail.order_summary.line_items
})

const productionList = computed(() => {
    if (!props.orderDetail.production) return []
    return props.orderDetail.production
})

const productionPlans = computed(() => {
    if (!props.orderDetail.production_plan) return []
    return props.orderDetail.production_plan
})

const planRedirectQuery = computed(() => {
    return {
        type: 'order-tracker-detail',
        sale_master_id: route.params.saleMasterId,
        tab: 'production',
        start: route.query.start,
        end: route.query.end
    }
})

const lockedOrder = computed(() => {
    const summary = props.orderDetail.order_summary
    if (!summary) return null
    return {
        id: summary.id,
        name: summary.bill_number,
        contact_profile_id: summary.contact_profile_id,
        contact_name: summary.contact_name
    }
})

const generalsOf = (production) => {
    if (!production.generals) return []
    return production.generals
}

const recordsByLineItemId = computed(() => {
    const map = {}
    productionList.value.forEach((production) => {
        generalsOf(production).filter((general) => general.type === 'production').forEach((general) => {
            if (!general.sales_order_general_id) return
            if (!map[general.sales_order_general_id]) map[general.sales_order_general_id] = []
            map[general.sales_order_general_id].push({ general, production })
        })
    })
    return map
})

const recordsOf = (item) => {
    if (!recordsByLineItemId.value[item.id]) return []
    return recordsByLineItemId.value[item.id]
}

const plansByLineItemId = computed(() => {
    const map = {}
    productionPlans.value.forEach((plan) => {
        if (!plan.sales_order_general_id) return
        if (!map[plan.sales_order_general_id]) map[plan.sales_order_general_id] = []
        map[plan.sales_order_general_id].push(plan)
    })
    return map
})

const plansOf = (item) => {
    if (!plansByLineItemId.value[item.id]) return []
    return plansByLineItemId.value[item.id]
}

const remainingQty = (item) => {
    const remaining = Number(item.quantity || 0) - Number(item.total_pq || 0)
    if (remaining < 0) return 0
    return remaining
}

const itemStatus = (item) => {
    if (!recordsOf(item).length) return 'pending'
    if (Number(item.total_pq || 0) >= Number(item.quantity || 0)) return 'completed'
    return 'in_progress'
}

const sumAmount = (rows) => {
    return rows.reduce((total, row) => total + parseFloat(row.amount || 0), 0)
}

const unlinkedSections = computed(() => {
    const generals = []
    productionList.value.forEach((production) => {
        generalsOf(production).forEach((general) => generals.push(general))
    })
    return SECTION_DEFS.map((def) => {
        const rows = generals.filter(def.match)
        return { key: def.key, title: def.title, rows, total: sumAmount(rows) }
    }).filter((section) => section.rows.length)
})

const readableDate = (date) => {
    if (!date) return ''
    return formatToDdMmYy(String(date).split(' ')[0])
}

const badgeStyle = (status) => {
    const color = taskStatusColors[status] || { bg: '#f9fafb', border: '#d1d5db', text: '#111827' }
    return { backgroundColor: color.bg, borderColor: color.border, color: color.text }
}

const progressWidth = (done, target) => {
    if (!target) return '0%'
    const ratio = (Number(done) / Number(target)) * 100
    if (ratio > 100) return '100%'
    return `${ratio.toFixed(0)}%`
}

const companyQuery = () => {
    return `?company_id=${route.params.companyId}`
}

const buildQrCodeQuery = (billNumber, refDate, userName) => {
    const qrcodeData = JSON.stringify({
        ref_no: billNumber,
        ref_date: refDate,
        created_by: userName,
        created_date_time: formatDateTime(new Date())
    })
    return `?code=${billNumber}&qrcode=${qrcodeData}`
}

const loadCompanyInfo = async () => {
    if (companyInfo.value.id) return
    await fetchCompanyInfo(route.params.companyId).then((res) => {
        companyInfo.value = res.data
    })
}

const onClickPrint = async (production) => {
    if (!production.id) return
    const user = store.state.user.user
    printing.value = true
    await loadCompanyInfo()

    await fetchSingleProduction(production.id, companyQuery()).then(async ({ status, data }) => {
        if (!status) return
        const barcodeQuery = buildQrCodeQuery(data.bill_number, data.production_date, user.name)
        await fetchBarcode(barcodeQuery).then((res) => {
            exportProductionPDF(companyInfo.value, data, res.barcode, res.qrcode, user.name)
        })
    })

    printing.value = false
}

const onOpenAddItemPlan = (item) => {
    editData.value = null
    lockedLineItem.value = {
        id: item.id,
        product_name: item.product_name,
        quantity: item.quantity,
        pcm_id: item.pcm_id,
        pcm_name: item.pcm_name,
        pcm_count: item.pcm_count
    }
    store.state.isModalOpen = true
}

const onViewPlan = async (plan) => {
    await fetchSingleProductionPlan(plan.id, companyQuery()).then((res) => {
        if (!res.status || !res.data) return
        singlePlan.value = res.data
        store.state.isModalOpenTwo = true
    }).catch(() => {
        singlePlan.value = null
    })
}

const onEditFromDetail = (plan) => {
    editData.value = plan
    lockedLineItem.value = null
    store.state.isModalOpenTwo = false
    store.state.isModalOpen = true
}

const onPlanSaved = () => {
    props.onPlanChanged()
}

onMounted(() => {
    store.state.isModalOpen = false
    store.state.isModalOpenTwo = false
})
</script>

<style scoped>
.general-card {
    min-height: auto;
}
.item-card {
    overflow: hidden;
    margin-bottom: 1.25rem;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}
.item-card-header {
    padding: 0.85rem 1rem;
    background-color: rgba(115, 103, 240, 0.06);
    border-bottom: 1px solid rgba(115, 103, 240, 0.14);
}
.item-title {
    font-size: 15px;
    font-weight: 600;
}
.item-card-body {
    padding: 0.75rem 1rem 1rem;
}
.nested-section {
    padding-left: 0.75rem;
    border-left: 2px solid rgba(115, 103, 240, 0.18);
}
.nested-section :deep(.section-card) {
    border-radius: 4px;
    box-shadow: none;
}
.nested-section :deep(.section-header) {
    padding: 8px 12px;
}
.records-title {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6c757d;
}
.production-record-row {
    padding: 0.6rem 0.75rem;
    border: 1px solid #eef0f5;
    border-radius: 4px;
    background-color: #fcfcfd;
    margin-bottom: 0.5rem;
}
.production-record-row:last-child {
    margin-bottom: 0;
}
.record-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #8a93a5;
}
.muted-text {
    font-size: 12px;
    color: #6c757d;
}
.fw-bold {
    font-weight: bold !important;
}
.status-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 12px;
}
.progress-track {
    width: 100%;
    max-width: 240px;
    height: 6px;
    border-radius: 3px;
    background-color: #e5e7eb;
}
.progress-fill {
    height: 100%;
    border-radius: 3px;
    background-color: #22c55e;
}
</style>
